// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0
import React, { useState } from 'react';

import SideNavigation, { SideNavigationProps } from '@cloudscape-design/components/side-navigation';

import { withLockedParams } from '../../common/locked-identity';

// Recurses because items can now be nested inside sections/expandable-link
// groups -- the old per-page flat `.map` only ever rewrote top-level hrefs.
function applyLockedParams(
  items: ReadonlyArray<SideNavigationProps.Item>,
  lockedOrg: string | null,
  lockedToken: string | null
): SideNavigationProps.Item[] {
  return items.map(item => {
    let next: SideNavigationProps.Item = item;
    if ('items' in item) {
      next = { ...next, items: applyLockedParams(item.items, lockedOrg, lockedToken) } as SideNavigationProps.Item;
    }
    if ('href' in item) {
      next = { ...next, href: withLockedParams(item.href, lockedOrg, lockedToken) } as SideNavigationProps.Item;
    }
    return next;
  });
}

export function buildActionsNavigationItems(options?: {
  webAppSetupExtraItems?: SideNavigationProps.Item[];
}): SideNavigationProps.Item[] {
  const webAppSetupItem: SideNavigationProps.Item = options?.webAppSetupExtraItems?.length
    ? {
        type: 'expandable-link-group',
        text: 'Web app setup',
        href: 'web-app-setup.html',
        defaultExpanded: true,
        items: options.webAppSetupExtraItems,
      }
    : { type: 'link', text: 'Web app setup', href: 'web-app-setup.html' };

  return [
    {
      type: 'section',
      text: 'Overview',
      defaultExpanded: true,
      items: [
        { type: 'link', text: 'Dashboard', href: '#/dashboard' },
        { type: 'link', text: 'Deployments', href: 'deployments.html' },
        { type: 'link', text: 'My apps', href: 'my-apps.html' },
      ],
    },
    {
      type: 'section',
      text: 'Apps',
      defaultExpanded: true,
      items: [
        { type: 'link', text: 'Streaming setup', href: 'wizard.html' },
        webAppSetupItem,
        { type: 'link', text: 'MCP', href: 'mcp.html' },
      ],
    },
    {
      type: 'section',
      text: 'Resources',
      defaultExpanded: true,
      items: [{ type: 'link', text: 'Crossplane resources', href: 'crossplane-resources.html' }],
    },
    {
      type: 'section',
      text: 'Security and Compliance',
      defaultExpanded: true,
      items: [{ type: 'link', text: 'Governance config', href: 'governance-config.html' }],
    },
  ];
}

export interface ActionsNavigationProps {
  activeHref: string;
  lockedOrg: string | null;
  lockedToken: string | null;
  webAppSetupExtraItems?: SideNavigationProps.Item[];
  // Page-specific interception (e.g. web-app-setup's go-httpbin actions) --
  // called before the default `#`-href preventDefault handling below, so a
  // page can preventDefault() and handle its own hrefs first.
  onFollow?: SideNavigationProps['onFollow'];
}

export const ActionsNavigation = ({
  activeHref,
  lockedOrg,
  lockedToken,
  webAppSetupExtraItems,
  onFollow,
}: ActionsNavigationProps) => (
  <SideNavigation
    header={{ text: 'Actions', href: '#/home' }}
    items={applyLockedParams(buildActionsNavigationItems({ webAppSetupExtraItems }), lockedOrg, lockedToken)}
    activeHref={activeHref}
    onFollow={event => {
      onFollow?.(event);
      if (event.defaultPrevented) {
        return;
      }
      if (event.detail.href?.startsWith('#')) {
        event.preventDefault();
      }
    }}
  />
);

// Bundled here (rather than each page rolling its own useState) so every
// page gets the same default via one import -- confirmed live 2026-08-16:
// the panel's open/closed state was previously left uncontrolled, so each
// page silently inherited a different Cloudscape default from its own
// unrelated `contentType` choice (`form`/`wizard` -> collapsed, `table`/
// `default` -> expanded), not a deliberate per-page decision.
export function useExpandedNavigation() {
  const [navigationOpen, setNavigationOpen] = useState(true);
  return {
    navigationOpen,
    onNavigationChange: ({ detail }: { detail: { open: boolean } }) => setNavigationOpen(detail.open),
  };
}
